import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LOGO_URL = 'https://storage.readdy-site.link/project_files/a1db2c25-97e2-4ceb-a4ec-c06941f029b5/a3ac0cdf-0792-4180-b72e-0df4bd1c7c5e_FMA-CREW-LOGO.png?v=b37410eb2aa41c47106263f8d4f50ddf';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const NAV_LINKS = [
    { label: t('nav.home'), href: '/', isHome: true },
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.about'), href: '#about' },
    { label: t('nav.why'), href: '#why' },
    { label: t('nav.portfolio'), href: '/portfolio', isRoute: true },
    { label: t('nav.faq'), href: '/faq', isRoute: true },
    { label: t('nav.contact'), href: '/contact', isRoute: true },
  ];

  const isKo = i18n.language === 'ko';

  const toggleLang = () => {
    i18n.changeLanguage(isKo ? 'en' : 'ko');
  };

  const handleHomeNav = () => {
    setMobileOpen(false);
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (href: string) => {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: href } });
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-white border-b border-gray-100' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="flex items-center cursor-pointer"
        >
          <div className="h-10 md:h-12 flex items-center">
            <img
              src={LOGO_URL}
              alt="FMA Crew - Facility Maintenance of America"
              className={`h-full w-auto object-contain transition-all duration-300 ${scrolled ? '' : 'brightness-0 invert'}`}
            />
          </div>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((link) => {
            if (link.isHome) {
              return (
                <a
                  key="home"
                  href="/"
                  onClick={(e) => { e.preventDefault(); handleHomeNav(); }}
                  className={`text-sm font-medium tracking-wide transition-colors duration-300 cursor-pointer ${
                    scrolled ? 'text-slate-600 hover:text-[#05294A]' : 'text-white/85 hover:text-[#92D7E8]'
                  }`}
                >
                  {link.label}
                </a>
              );
            }
            return link.isRoute ? (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 cursor-pointer ${
                  scrolled ? 'text-slate-600 hover:text-[#05294A]' : 'text-white/85 hover:text-[#92D7E8]'
                }`}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 cursor-pointer ${
                  scrolled ? 'text-slate-600 hover:text-[#05294A]' : 'text-white/85 hover:text-[#92D7E8]'
                }`}
              >
                {link.label}
              </a>
            );
          })}

          {/* Language Toggle */}
          <button
            onClick={toggleLang}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold tracking-wider transition-all duration-300 cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'border-slate-300 text-slate-600 hover:border-[#05294A] hover:text-[#05294A]'
                : 'border-white/30 text-white/80 hover:border-white hover:text-white'
            }`}
            aria-label="Toggle language"
          >
            <i className="ri-translate-2 text-sm" />
            <span>{isKo ? 'EN' : '한국어'}</span>
          </button>

          <Link
            to="/contact"
            className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
              scrolled
                ? 'bg-[#05294A] text-white hover:bg-[#032240]'
                : 'bg-white/15 text-white border border-white/30 hover:bg-[#05294A] hover:border-[#05294A]'
            }`}
          >
            {t('nav.cta')}
          </Link>
        </nav>

        {/* Mobile right side */}
        <div className="md:hidden flex items-center gap-2">
          {/* Mobile Language Toggle */}
          <button
            onClick={toggleLang}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-xs font-bold tracking-wider transition-all duration-300 cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'border-slate-300 text-slate-600'
                : 'border-white/30 text-white/80'
            }`}
            aria-label="Toggle language"
          >
            <i className="ri-translate-2 text-sm" />
            <span>{isKo ? 'EN' : 'KO'}</span>
          </button>

          {/* Mobile hamburger */}
          <button
            className={`w-9 h-9 flex items-center justify-center cursor-pointer rounded-lg transition-colors ${
              scrolled ? 'text-slate-900 hover:bg-slate-100' : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={mobileOpen ? 'ri-close-line text-2xl' : 'ri-menu-3-line text-2xl'} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="md:hidden border-t border-white/10 px-4 py-5 flex flex-col gap-1"
          style={{
            backgroundColor: '#05294A',
            backgroundImage: 'radial-gradient(circle, rgba(146, 215, 232, 0.18) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
        >
          {NAV_LINKS.map((link) => {
            if (link.isHome) {
              return (
                <a
                  key="home"
                  href="/"
                  onClick={(e) => { e.preventDefault(); handleHomeNav(); }}
                  className="text-sm text-white/80 font-medium hover:text-[#92D7E8] hover:bg-white/10 px-4 py-3 rounded-lg transition-all cursor-pointer"
                >
                  {link.label}
                </a>
              );
            }
            return link.isRoute ? (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-sm text-white/80 font-medium hover:text-[#92D7E8] hover:bg-white/10 px-4 py-3 rounded-lg transition-all cursor-pointer"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
                className="text-sm text-white/80 font-medium hover:text-[#92D7E8] hover:bg-white/10 px-4 py-3 rounded-lg transition-all cursor-pointer"
              >
                {link.label}
              </a>
            );
          })}
          <div className="pt-3 mt-2 border-t border-white/15">
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="block w-full px-5 py-3 bg-[#92D7E8] text-[#05294A] rounded-lg text-sm font-bold text-center whitespace-nowrap cursor-pointer hover:bg-white transition-colors"
            >
              {t('nav.cta')}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
